import React, { useContext } from 'react';
import {
  useIsMissingPortCostVisibleState,
  useIsVesselCapacityVisibleState,
  useIsVesselRemarksVisibleState,
  useIsViolationVoyagesHighlightedState,
  useIsVoyageCapacityVisibleState,
  useIsVoyageCargoVisibleState,
  useIsVoyageChartererVisibleState,
  usePlanningLayoutState,
  useVoyageBoxVCPState,
  useViolationIndicatorBasisState,
  useVoyageBoxTCEState,
  useRegionToolIntervalState,
  useRegionToolAmountsMTSVisibleState,
  useRegionToolAmountsCBMVisibleState,
  useIsWaitLaycanDefaultState,
  useIsVoyageAttributeVisibleState,
  useVoyageBoxProfitState,
} from '../state/planning/options';
import { MenuToggle } from './ui/Menu/Menu';
import { Text, Box } from '../components/ui';

interface ToggleStyleProps {
  isMobileView?: boolean;
}

const ToggleStyleContext = React.createContext<ToggleStyleProps | undefined>(
  undefined,
);

const createSettingGroup =
  (title: string, children: JSX.Element) =>
  (props?: ToggleStyleProps): JSX.Element => {
    const styleProps = props?.isMobileView
      ? {
          width: '16px',
          height: '16px',
          mt: '2px',
          isMobileView: true,
        }
      : undefined;
    return (
      <Box
        display="flex"
        flexDirection="column"
        mt={props?.isMobileView ? 2 : 0}
      >
        <Text fontWeight="bold" mb={1} mt={2}>
          {title}
        </Text>
        <ToggleStyleContext.Provider value={styleProps}>
          {children}
        </ToggleStyleContext.Provider>
      </Box>
    );
  };

const createSettingToggle =
  (
    label: string,
    useState: () => [boolean, (newVal: boolean) => void],
    title?: string,
  ) =>
  (): JSX.Element => {
    const [isVisible, setIsVisible] = useState();
    const styleProps = useContext(ToggleStyleContext);

    return (
      <MenuToggle
        type="checkbox"
        label={label}
        checked={isVisible}
        title={title}
        onChange={() => setIsVisible(!isVisible)}
        {...styleProps}
      />
    );
  };

function LayoutToggle(): JSX.Element {
  const [layout, setLayout] = usePlanningLayoutState();

  return (
    <>
      <MenuToggle
        type="radio"
        label="Horizontal order/timeline split"
        checked={layout === 'horizontal'}
        onChange={(e) => {
          if (e.target.checked) {
            setLayout('horizontal');
          }
        }}
      />
      <MenuToggle
        type="radio"
        label="Vertical order/timeline split"
        checked={layout === 'vertical'}
        onChange={(e) => {
          if (e.target.checked) {
            setLayout('vertical');
          }
        }}
      />
    </>
  );
}

function ViolationIndicatorBasisToggle(): JSX.Element {
  const [violationBasis, setViolationBasis] = useViolationIndicatorBasisState();
  const styleProps = useContext(ToggleStyleContext);
  return (
    <>
      <MenuToggle
        type="radio"
        label="Show from ETA"
        checked={violationBasis === 'eta'}
        onChange={(e) => {
          if (e.target.checked) {
            setViolationBasis('eta');
          }
        }}
        {...styleProps}
      />
      <MenuToggle
        type="radio"
        label="Show from operations start"
        checked={violationBasis === 'operation_start'}
        onChange={(e) => {
          if (e.target.checked) {
            setViolationBasis('operation_start');
          }
        }}
        {...styleProps}
      />
      <MenuToggle
        type="radio"
        label="Hide violations"
        checked={violationBasis === 'none'}
        onChange={(e) => {
          if (e.target.checked) {
            setViolationBasis('none');
          }
        }}
        {...styleProps}
      />
    </>
  );
}

export const LayoutOptions = createSettingGroup(
  'Planning page layout',
  <LayoutToggle />,
);

const VoyageBoxCargoDetailsToggle = createSettingToggle(
  'Cargo details (default)',
  useIsVoyageCargoVisibleState,
);
const VoyageBoxCapacityToggle = createSettingToggle(
  'Available capacity',
  useIsVoyageCapacityVisibleState,
);
const VoyageBoxAttributeToggle = createSettingToggle(
  'Show voyage attribute',
  useIsVoyageAttributeVisibleState,
);
const VoyageBoxChartererToggle = createSettingToggle(
  'Charterer',
  useIsVoyageChartererVisibleState,
);
const VoyageBoxMissingPortCostToggle = createSettingToggle(
  'Missing Port Cost',
  useIsMissingPortCostVisibleState,
);
const VoyageBoxTCEToggle = createSettingToggle('TCE', useVoyageBoxTCEState);

const VoyageBoxVCPToggle = createSettingToggle(
  'VCP (P)',
  useVoyageBoxVCPState,
  'Voyage Cargo Performance',
);

const VoyageBoxProfitToggle = createSettingToggle(
  'Voyage Profit',
  useVoyageBoxProfitState,
  'Voyage Profit',
);

const RegionToolAmountsMTSToggle = createSettingToggle(
  'Show tonnage and cargo amounts (mts)',
  useRegionToolAmountsMTSVisibleState,
);

const RegionToolAmountsCBMToggle = createSettingToggle(
  'Show tonnage and cargo amounts (cbm)',
  useRegionToolAmountsCBMVisibleState,
);

const RegionToolIntervalToggle = (): JSX.Element => {
  const [interval, setInterval] = useRegionToolIntervalState();
  const styleProps = useContext(ToggleStyleContext);

  return (
    <MenuToggle
      type="checkbox"
      label={'Use months'}
      checked={interval === 'months'}
      title={'When unchecked, weeks'}
      onChange={(e) => {
        setInterval(e.target.checked ? 'months' : 'weeks');
      }}
      {...styleProps}
    />
  );
};

export const RegionToolOptions = createSettingGroup(
  'Region tool',
  <>
    <RegionToolAmountsMTSToggle />
    <RegionToolAmountsCBMToggle />
    <RegionToolIntervalToggle />
  </>,
);

export const VoyageBoxFieldOptions = createSettingGroup(
  'Voyage box display settings',
  <>
    <VoyageBoxAttributeToggle />
    <VoyageBoxCargoDetailsToggle />
    <VoyageBoxCapacityToggle />
    <VoyageBoxChartererToggle />
    <VoyageBoxMissingPortCostToggle />
    <VoyageBoxTCEToggle />
    <VoyageBoxVCPToggle />
    <VoyageBoxProfitToggle />
  </>,
);

const VesselCapacityToggle = createSettingToggle(
  'Capacity',
  useIsVesselCapacityVisibleState,
);
const VesselRemarksToggle = createSettingToggle(
  'Remarks',
  useIsVesselRemarksVisibleState,
);

export const VesselDataFieldOptions = createSettingGroup(
  'Vessel data settings',
  <>
    <VesselCapacityToggle />
    <VesselRemarksToggle />
  </>,
);

const ViolationsHighlightToggle = createSettingToggle(
  'Highlight voyages with violations',
  useIsViolationVoyagesHighlightedState,
);

export const ViolationDisplayOptions = createSettingGroup(
  'Violations',
  <>
    <ViolationsHighlightToggle />
    <ViolationIndicatorBasisToggle />
  </>,
);

const WaitLaycanToggle = createSettingToggle(
  'Wait for laycan by default',
  useIsWaitLaycanDefaultState,
);

export const AssignmentBehaviorOptions = createSettingGroup(
  'Assignments behavior',
  <>
    <WaitLaycanToggle />
  </>,
);

/** Pressing shift reverses the default behavior */
export const shouldWaitForLaycan = (
  isShiftPressed: boolean,
  isWaitDefault: boolean,
): boolean => (isShiftPressed ? !isWaitDefault : isWaitDefault);
